import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const ApplicationManagementForm = ({ formData, errors, onChange }) => {
  const [newQuestion, setNewQuestion] = useState('');
  const [questionType, setQuestionType] = useState('text');

  const questionTypeOptions = [
    { value: 'text', label: 'Short Text' },
    { value: 'paragraph', label: 'Paragraph' },
    { value: 'yes-no', label: 'Yes/No' },
    { value: 'multiple-choice', label: 'Multiple Choice' }
  ];

  const handleAddQuestion = () => {
    if (newQuestion?.trim()) {
      const question = {
        id: Date.now(),
        question: newQuestion,
        type: questionType,
        required: true
      };
      onChange('screeningQuestions', [...(formData?.screeningQuestions || []), question]);
      setNewQuestion('');
      setQuestionType('text');
    }
  };

  const handleRemoveQuestion = (id) => {
    const updated = formData?.screeningQuestions?.filter((q) => q?.id !== id);
    onChange('screeningQuestions', updated);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today?.getFullYear();
    const month = String(today?.getMonth() + 1)?.padStart(2, '0');
    const day = String(today?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Application Deadline"
          type="date"
          value={formData?.applicationDeadline}
          onChange={(e) => onChange('applicationDeadline', e?.target?.value)}
          error={errors?.applicationDeadline}
          min={getTodayDate()}
          required
        />
        <Input
          label="Maximum Candidates"
          type="number"
          placeholder="e.g., 50"
          value={formData?.candidateLimit}
          onChange={(e) => onChange('candidateLimit', e?.target?.value)}
          description="Leave blank for unlimited applications"
        />
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium text-card-foreground mb-4">
          Screening Questions
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Add custom questions to screen candidates during the application process. These questions help you identify the most suitable candidates.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-6">
              <Input
                type="text"
                placeholder="Enter your screening question"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddQuestion();
                  }
                }}
              />
            </div>
            <div className="md:col-span-4">
              <Select
                options={questionTypeOptions}
                value={questionType}
                onChange={(value) => setQuestionType(value)}
                placeholder="Question type"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                onClick={handleAddQuestion}
                iconName="Plus"
                iconPosition="left"
                fullWidth
              >
                Add
              </Button>
            </div>
          </div>

          {formData?.screeningQuestions?.length > 0 && (
            <div className="space-y-3">
              {formData?.screeningQuestions?.map((q, index) => (
                <div
                  key={q?.id}
                  className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border border-border"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground mb-1">
                      {q?.question}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="px-2 py-0.5 bg-background rounded">
                        {questionTypeOptions?.find((opt) => opt?.value === q?.type)?.label}
                      </span>
                      {q?.required && (
                        <span className="px-2 py-0.5 bg-error/10 text-error rounded">
                          Required
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveQuestion(q?.id)}
                    className="flex-shrink-0 p-2 rounded-md hover:bg-destructive/10 text-destructive transition-smooth"
                  >
                    <Icon name="Trash2" size={16} color="currentColor" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {formData?.screeningQuestions?.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
              <Icon name="HelpCircle" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No screening questions added yet. Add questions to filter candidates effectively.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
            <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-1">
              Pro Tips for Screening Questions
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Keep questions clear and specific to the role</li>
              <li>• Ask about relevant experience and skills</li>
              <li>• Include scenario-based questions for better assessment</li>
              <li>• Limit to 3-5 questions to avoid overwhelming candidates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationManagementForm;